// Memory fallback when localStorage is not available
const memoryStorage = new Map();

/**
 * Safe storage utility that falls back to memory storage when localStorage is not available
 * Useful for handling incognito mode, cross-origin iframes, and other restricted environments
 */
export const safeStorage = {
    /**
     * Safely get an item from storage
     * @param {string} key - The key to retrieve
     * @param {any} defaultValue - Optional default value if key doesn't exist
     * @returns {any} The stored value or null/defaultValue if not found
     */
    get: (key, defaultValue = null) => {
        try {
            // Try localStorage first
            if (typeof window !== 'undefined' && window.localStorage) {
                const item = window.localStorage.getItem(key);
                if (item !== null) {
                    try {
                        return JSON.parse(item);
                    } catch {
                        return item; // Return as is if not JSON
                    }
                }
            }
        } catch {
            console.warn('localStorage not available, using memory storage');
        }
        // Fallback to memory storage
        return memoryStorage.get(key) ?? defaultValue;
    },

    /**
     * Safely set an item in storage
     * @param {string} key - The key to store under
     * @param {any} value - The value to store
     */
    set: (key, value) => {
        try {
            // Try localStorage first
            if (typeof window !== 'undefined' && window.localStorage) {
                const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);
                window.localStorage.setItem(key, serializedValue);
                return;
            }
        } catch {
            console.warn('localStorage not available, using memory storage');
        }
        // Fallback to memory storage
        memoryStorage.set(key, value);
    },

    /**
     * Safely remove an item from storage
     * @param {string} key - The key to remove
     */
    remove: (key) => {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                window.localStorage.removeItem(key);
            }
        } catch {
            console.warn('localStorage not available, using memory storage');
        }
        memoryStorage.delete(key);
    },

    /**
     * Safely clear all items from storage
     */
    clear: () => {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                window.localStorage.clear();
            }
        } catch {
            console.warn('localStorage not available, using memory storage');
        }
        memoryStorage.clear();
    },

    /**
     * Check if storage is available
     * @returns {boolean} True if either localStorage or memory storage is available
     */
    isAvailable: () => {
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                const testKey = '__storage_test__';
                window.localStorage.setItem(testKey, testKey);
                window.localStorage.removeItem(testKey);
                return true;
            }
        } catch {
            return false;
        }
        return true; // Memory storage is always available
    }
}; 